import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Learner, UploadRecord } from '../interfaces/LearnerInterfaces';

interface LearnerState {
  learners: Learner[];
  uploadRecords: UploadRecord[];
  loading: boolean;
  error: string | null;
  selectedLearner: Learner | null;
  searchQuery: string;
  totalCount: number;
}

type LearnerAction =
  | { type: 'SET_LEARNERS'; payload: { records: Learner[]; total: number } }
  | { type: 'ADD_LEARNER'; payload: Learner }
  | { type: 'UPDATE_LEARNER'; payload: Learner }
  | { type: 'DELETE_LEARNER'; payload: string }
  | { type: 'SET_UPLOAD_RECORDS'; payload: UploadRecord[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_LEARNER'; payload: Learner | null }
  | { type: 'SET_SEARCH_QUERY'; payload: string };

const initialState: LearnerState = {
  learners: [],
  uploadRecords: [],
  loading: false,
  error: null,
  selectedLearner: null,
  searchQuery: '',
  totalCount: 0,
};

function learnerReducer(state: LearnerState, action: LearnerAction): LearnerState {
  switch (action.type) {
    case 'SET_LEARNERS':
      return { ...state, learners: action.payload.records, totalCount: action.payload.total, loading: false };
    case 'ADD_LEARNER':
      return { ...state, learners: [action.payload, ...state.learners], totalCount: state.totalCount + 1 };
    case 'UPDATE_LEARNER':
      return {
        ...state,
        learners: state.learners.map((l) =>
          l.email_address === action.payload.email_address ? action.payload : l
        ),
      };
    case 'DELETE_LEARNER':
      return {
        ...state,
        learners: state.learners.filter((l) => l.email_address !== action.payload),
        totalCount: Math.max(0, state.totalCount - 1),
      };
    case 'SET_UPLOAD_RECORDS':
      return { ...state, uploadRecords: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_SELECTED_LEARNER':
      return { ...state, selectedLearner: action.payload };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    default:
      return state;
  }
}

interface LearnerContextType {
  state: LearnerState;
  dispatch: React.Dispatch<LearnerAction>;
}

const LearnerContext = createContext<LearnerContextType | undefined>(undefined);

export const LearnerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(learnerReducer, initialState);
  return (
    <LearnerContext.Provider value={{ state, dispatch }}>
      {children}
    </LearnerContext.Provider>
  );
};

export const useLearnerContext = (): LearnerContextType => {
  const context = useContext(LearnerContext);
  if (!context) throw new Error('useLearnerContext must be used within LearnerProvider');
  return context;
};
